import React, { useCallback, useMemo } from 'react'
import { Modal, Form, Input, Checkbox } from 'antd'
import SelectFromEnum from '../../../../components/select-from-enum'

interface ILegalData {
  id?: number | null
  name?: string
  organization_from?: number
  address?: string
  director?: string
  inn?: string
  invoice_curr?: string
  invoice_corr?: string
  bank_name?: string
  bik?: string
  okpo?: string
  kpp?: string
  ogrn?: string
  active?: boolean
  certificate_number?: string
}

interface AddRequisitesModalProps {
  visible: boolean
  onCancel: () => void
  onSave: (data: ILegalData) => void
  editingData?: ILegalData | null
  isSaving?: boolean
}

const AddRequisitesModal: React.FC<AddRequisitesModalProps> = ({
  visible,
  onCancel,
  onSave,
  editingData,
  isSaving = false,
}) => {
  const [form] = Form.useForm()

  React.useEffect(() => {
    if (visible && editingData) {
      form.setFieldsValue(editingData)
    } else if (!visible) {
      form.resetFields()
    }
  }, [visible, editingData, form])

  const organizationFromSchema = useMemo(
    () => ({
      type: 'string',
      title: 'Юр. статус',
      enum: [1, 2, 3],
      oneOf: [
        { const: 1, title: 'ООО' },
        { const: 2, title: 'ИП' },
        { const: 3, title: 'АНО' },
      ],
      view: { name: 'select-from-enum' },
    }),
    [],
  )

  const organizationFromUISchema = useMemo(
    () => ({
      options: {
        width: '100%',
        placeholder: 'Выберите тип организации',
        allowClear: true,
        showSearch: true,
      },
    }),
    [],
  )

  const handleOk = useCallback(async () => {
    try {
      const values = await form.validateFields()
      onSave({
        ...values,
        id: editingData?.id,
        active: values.active !== false,
      })
      form.resetFields()
    } catch (errorInfo) {
      console.error('Validation Failed:', errorInfo)
    }
  }, [form, onSave, editingData])

  const handleCancel = useCallback(() => {
    form.resetFields()
    onCancel()
  }, [form, onCancel])

  const organizationFrom = Form.useWatch('organization_from', form)
  const isCertificateRequired = !(organizationFrom === 1)

  return (
    <Modal
      title={editingData ? 'Редактировать банковские реквизиты' : 'Добавить банковские реквизиты'}
      open={visible}
      onOk={handleOk}
      onCancel={handleCancel}
      width={600}
      okText={editingData ? 'Сохранить' : 'Добавить'}
      cancelText="Отмена"
      confirmLoading={isSaving}
      destroyOnHidden={true}
    >
      <Form form={form} layout="vertical" initialValues={{ active: true }}>
        <Form.Item
          name="name"
          label="Название"
          rules={[{ required: true, message: 'Пожалуйста, укажите название' }]}
        >
          <Input placeholder="Введите название" />
        </Form.Item>

        <Form.Item
          name="organization_from"
          label="Юр. статус"
          rules={[{ required: true, message: 'Пожалуйста, выберите юридический статус' }]}
        >
          <SelectFromEnum schema={organizationFromSchema} UISchema={organizationFromUISchema} />
        </Form.Item>

        <Form.Item
          name="address"
          label="Адрес"
          rules={[{ required: true, message: 'Пожалуйста, укажите адрес' }]}
        >
          <Input placeholder="Введите адрес" />
        </Form.Item>

        <Form.Item
          name="director"
          label="Генеральный директор"
          rules={[{ required: true, message: 'Пожалуйста, укажите генерального директора' }]}
        >
          <Input placeholder="Введите ФИО генерального директора" />
        </Form.Item>

        <Form.Item
          name="inn"
          label="ИНН"
          rules={[{ required: true, message: 'Пожалуйста, укажите ИНН' }]}
        >
          <Input placeholder="Введите ИНН" />
        </Form.Item>

        <Form.Item
          name="certificate_number"
          label="Номер свидетельства"
          rules={[
            { required: isCertificateRequired, message: 'Пожалуйста, укажите номер свидетельства' },
          ]}
        >
          <Input placeholder="Введите номер свидетельства" />
        </Form.Item>

        <Form.Item
          name="invoice_curr"
          label="Расчетный счет"
          rules={[{ required: true, message: 'Пожалуйста, укажите расчетный счет' }]}
        >
          <Input placeholder="Введите расчетный счет" />
        </Form.Item>

        <Form.Item
          name="invoice_corr"
          label="Корреспондентский счет"
          rules={[{ required: true, message: 'Пожалуйста, укажите корреспондентский счет' }]}
        >
          <Input placeholder="Введите корреспондентский счет" />
        </Form.Item>

        <Form.Item
          name="bank_name"
          label="Название банка"
          rules={[{ required: true, message: 'Пожалуйста, укажите название банка' }]}
        >
          <Input placeholder="Введите название банка" />
        </Form.Item>

        <Form.Item
          name="bik"
          label="БИК"
          rules={[{ required: true, message: 'Пожалуйста, укажите БИК' }]}
        >
          <Input placeholder="Введите БИК" />
        </Form.Item>

        <Form.Item
          name="okpo"
          label="ОКПО"
          rules={[{ required: true, message: 'Пожалуйста, укажите ОКПО' }]}
        >
          <Input placeholder="Введите ОКПО" />
        </Form.Item>

        <Form.Item
          name="kpp"
          label="КПП"
          rules={[{ required: true, message: 'Пожалуйста, укажите КПП' }]}
        >
          <Input placeholder="Введите КПП" />
        </Form.Item>

        <Form.Item
          name="ogrn"
          label="ОГРН"
          rules={[{ required: true, message: 'Пожалуйста, укажите ОГРН' }]}
        >
          <Input placeholder="Введите ОГРН" />
        </Form.Item>

        <Form.Item name="active" valuePropName="checked">
          <Checkbox>Активен</Checkbox>
        </Form.Item>
      </Form>
    </Modal>
  )
}

export default React.memo(AddRequisitesModal)
